/**
 * @fileoverview Refresh client-side do catálogo (#8 do roadmap pós-MVP).
 *
 * Backend tem worker `catalog-refresh` rodando 30min — re-popula Redis
 * com últimas listas Xtream. Cliente em sessão longa não sabe e mostra
 * snapshot velho até reload manual.
 *
 * Este hook resolve em 2 mecanismos combinados (decisão 2026-05-08):
 * - **A: Polling 30min com jitter ±5min** — cobre user que fica grudado
 *   num filme/série 4h+ sem voltar à home.
 * - **C: Visibility refresh** — ao voltar de standby da TV, dispara
 *   revalidação se passou >30min desde a última.
 *
 * Backend já implementa ETag/If-None-Match no `/v1/catalog/snapshot`
 * (ver `apps/api/src/routes/authenticated.ts`). Em 99% dos casos backend
 * retorna 304 e só renovamos `storedAt` no IDB. Quando muda, baixa novo
 * snapshot, cliente re-renderiza silencioso (UX decisão: sem toast).
 *
 * `loadSnapshot` já verifica TTL local (24h) — chamadas em <24h sem
 * `force: true` retornam do cache sem hit no backend, então o hook
 * sempre passa `force: true` pra forçar a revalidação contra origem.
 *
 * Não interfere em playback/screensaver/UI: ambos os mecanismos são
 * apenas fetch em background. Em playback ativo, `<video>` em
 * foreground bloqueia screensaver via API nativa do device.
 */

import { useCapability } from '@app-iptv/device/react'
import { useEffect, useRef } from 'react'

import { loadSnapshot } from '../catalog-snapshot'
import { catalogStore } from '../catalog-store'
import { orFallbackAfter } from '../lib/deadline'
import { track } from '../qoe'

/** Jitter ±5min pra dispersar load quando muitos clientes abrem o app
 *  no mesmo minuto (evita spike sincronizado). */
const POLL_JITTER_MS = 5 * 60 * 1000

/** Prazo do `getMeta` do primeiro tick. O `catch` ali embaixo cobre IDB que
 *  REJEITA; IDB que não responde (pendurado no `blocked`) não é erro, é
 *  silêncio — e sem prazo o `session-start` simplesmente nunca acontecia,
 *  empurrando a primeira revalidação pro poll de 30min. */
const META_READ_TIMEOUT_MS = 5_000

/** Calcula próximo intervalo: capability base ± jitter. Random uniforme. */
function nextPollInterval(baseMs: number): number {
  const jitter = (Math.random() * 2 - 1) * POLL_JITTER_MS
  return baseMs + jitter
}

interface UseCatalogRefreshOpts {
  /** Provider key (NÃO inclui profile — snapshot é shared cross-profile
   *  dentro do mesmo provider). Mudança de providerKey reseta timer
   *  (logout/troca de provider).
   *
   *  ⚠️ `null` DESLIGA o hook inteiro — sem tick de 5s, sem poll, sem refresh de
   *  standby. Como este é o único caminho automático de (re)carga do snapshot,
   *  `null` significa literalmente "app sem catálogo até alguém recarregar".
   *  Quem calcula esta chave é o `useCatalogRefreshProviderKey`, e a condição
   *  dele só pode depender de estado com TERMINAÇÃO GARANTIDA — ver o
   *  fileoverview de lá. */
  providerKey: string | null
}

/**
 * Hook que orquestra revalidação periódica + visibility do snapshot.
 *
 * Idempotente — se scope não mudou, mantém timer; se mudou, reseta.
 *
 * Não bloqueia render. Falhas são logged em telemetria (`result: 'error'`)
 * e não propagam — UI continua mostrando o snapshot atual.
 */
export function useCatalogRefresh({
  providerKey,
}: UseCatalogRefreshOpts): void {
  /** Timestamp ms do último revalidate bem-sucedido (qualquer source).
   *  Usado pelo visibility handler pra evitar refetch logo após poll. */
  const lastRevalidatedAtRef = useRef<number>(0)
  /** Cadência base do polling — capability-aware. Match com worker
   *  backend (30min) em rede saudável; estendido pra 60min em
   *  network 'poor' pra não competir com streaming. */
  const intervalMs = useCapability('catalogRefreshIntervalMs')
  /** Threshold mínimo entre revalidações disparadas por visibility —
   *  match com cadência atual pra evitar dupla. */
  const visibilityMinIntervalMs = intervalMs

  useEffect(() => {
    if (!providerKey) return

    let cancelled = false

    const revalidate = async (
      source: 'poll' | 'visibility' | 'session-start',
    ): Promise<void> => {
      const start = performance.now()
      try {
        // force=true pra furar TTL local (24h) e bater no backend.
        // Backend responde 304 se ETag bater (caso comum), 200 se mudou.
        const r = await loadSnapshot(providerKey, { force: true })
        if (cancelled) return
        lastRevalidatedAtRef.current = Date.now()
        const result =
          r.source === 'not-modified'
            ? '304'
            : r.source === 'fetch'
              ? '200'
              : 'cache'
        track({
          type: 'catalog_revalidated',
          ts: Math.floor(Date.now() / 1000),
          source,
          result,
          providerScope: providerKey,
          elapsedMs: Math.round(performance.now() - start),
        })
      } catch {
        if (cancelled) return
        track({
          type: 'catalog_revalidated',
          ts: Math.floor(Date.now() / 1000),
          source,
          result: 'error',
          providerScope: providerKey,
          elapsedMs: Math.round(performance.now() - start),
        })
        // Falha silenciosa: snapshot atual continua válido em memória.
      }
    }

    // Primeiro tick: revalida 5s após mount (não compete com bootstrap).
    // Pula se snapshot atual no IDB já tem <intervalMs — o fetch de boot
    // (`boot-snapshot.ts`) costuma deixar snapshot fresco e o session-start
    // seria round-trip gratuito (sempre 304). Em rede saudável é desperdício de
    // ~30ms; em vlow Tizen, custo de fetch+parse-de-headers acumula.
    // Este skip funciona de verdade (lê `storedAt` do IDB, que o fetch de boot
    // grava) — ao contrário do skip inicial do `useBandwidthRefresh`, que
    // depende de `getCachedBandwidth()` e nunca dispara por este caminho.
    const initialTimer = setTimeout(async () => {
      if (cancelled) return
      if (document.visibilityState !== 'visible') return
      try {
        // getMeta: só precisamos do storedAt. Ler o record inteiro custaria a
        // desserialização do catálogo completo por um timestamp.
        const meta = await orFallbackAfter(
          catalogStore.getMeta(providerKey),
          META_READ_TIMEOUT_MS,
          null,
        )
        if (cancelled) return
        if (meta) {
          const ageMs = Date.now() - meta.storedAt * 1000
          if (ageMs < intervalMs) {
            // Snapshot recém-gravado pelo overlay — registra a mão e pula.
            lastRevalidatedAtRef.current = meta.storedAt * 1000
            return
          }
        }
      } catch {
        // IDB indisponível — segue pro revalidate normal.
      }
      void revalidate('session-start')
    }, 5_000)

    // Polling com jitter — encadeia (await) pra próximo poll só agendar
    // após fetch atual resolver. Em rede saudável overhead é nulo
    // (<1s); em cauda longa (TLS travado, backend instável), evita
    // sobreposição de fetches.
    // Quando hidden (TV em standby ou tab oculta), pula a revalidação
    // mas re-agenda — ao voltar de standby, próximo tick acontece no
    // intervalo normal. Isso evita: (a) bursts de fetch ao retomar
    // após standby longo (timers JS engine podem empilhar dependendo
    // do device); (b) tráfego desnecessário com user fora.
    let pollTimer: ReturnType<typeof setTimeout> | null = null
    const schedulePoll = (): void => {
      pollTimer = setTimeout(async () => {
        if (document.visibilityState === 'visible') {
          await revalidate('poll')
        }
        if (!cancelled) schedulePoll()
      }, nextPollInterval(intervalMs))
    }
    schedulePoll()

    // Visibility: ao voltar de standby, revalida se passou > intervalo
    // base. TV vem de standby → document.visibilityState muda pra
    // 'visible'. Threshold evita dupla revalidação se poll acabou
    // de rodar. Quando 'hidden', NÃO faz nada (early return) — o
    // scheduler de poll também respeita visibilityState.
    const onVisibility = (): void => {
      if (document.visibilityState !== 'visible') return
      const sinceLast = Date.now() - lastRevalidatedAtRef.current
      if (sinceLast < visibilityMinIntervalMs) return
      void revalidate('visibility')
    }
    document.addEventListener('visibilitychange', onVisibility)

    return () => {
      cancelled = true
      clearTimeout(initialTimer)
      if (pollTimer) clearTimeout(pollTimer)
      document.removeEventListener('visibilitychange', onVisibility)
    }
  }, [providerKey, intervalMs, visibilityMinIntervalMs])
}
